/**
 * This file defines the interfaces for communication between the White Label server side and the Google Analytics API.
 * These types have been built based on the Google Analytics API reference:
 * https://developers.google.com/analytics/devguides/collection/ga4/reference/events
 */

import {
  AddPaymentInfoParams,
  AddToCartParams,
  BeginCheckoutParams,
  Item,
  LocationSuggestionParams,
  PurchaseParams,
  SelectItemParams,
  SelectPromotionParams,
  ViewCartParams,
  ViewItemListParams,
  ViewItemParams,
  ViewPromotionParams,
} from '@rewardops/utils'

export enum GoogleAnalyticEvents {
  SelectItem = 'select_item',
  ViewItem = 'view_item',
  ViewItemList = 'view_item_list',
  BeginCheckout = 'begin_checkout',
  Purchase = 'purchase',
  ViewPromotion = 'view_promotion',
  SelectPromotion = 'select_promotion',
}

// The list of fields could be extended based on:
// https://developers.google.com/analytics/devguides/collection/ga4/reference/events?sjid=6448810843497618047-NC&client_type=gtag#select_item_item
export interface GoogleItem {
  item_id: string
  item_name: string
  item_brand?: string
  item_category?: string
  price?: number
  quantity?: number
  discount?: number
  coupon?: string
}

interface IGoogleEventParams {
  items: GoogleItem[]
}

interface IGoogleSelectItemParams extends IGoogleEventParams {
  points?: number
  item_list_id?: string
  item_list_name?: string
}

interface IGoogleViewItemParams extends IGoogleEventParams {
  currency?: string
  value?: number
  recommended?: boolean
}

interface IGoogleViewCartParams extends IGoogleEventParams {
  currency?: string
  value?: number
  cart_item_count: number
  points: number
  device_type: string
}

interface IGoogleViewItemListParams extends IGoogleEventParams {
  item_list_id?: string
  item_list_name?: string
}

interface IGoogleBeginCheckoutParams extends IGoogleEventParams {
  currency: string
  value: number
  coupon?: string
  time_spent_on_cart: number
}

interface IGoogleAddToCartParams extends IGoogleEventParams {
  currency: string
  value: number
  coupon?: string
}

interface IGooglePurchaseParams extends IGoogleEventParams {
  currency: string
  value: number
  transaction_id: string
  coupon?: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
}

interface IGoogleAddPaymentInfoParams extends IGoogleEventParams {
  currency: string
  value: number
  payment_type: string
  tax_amount: number
}

interface IGoogleViewPromotionParams extends IGoogleEventParams {
  creative_name?: string
  creative_slot?: string
  promotion_id?: string
  promotion_name?: string
}

interface IGoogleSelectPromotionParams extends IGoogleEventParams {
  creative_name?: string
  creative_slot?: string
  promotion_id?: string
  promotion_name?: string
}

export type GoogleEventPayload =
  | IGoogleSelectItemParams
  | IGoogleViewItemParams
  | IGoogleViewItemListParams
  | IGoogleBeginCheckoutParams
  | IGooglePurchaseParams
  | IGoogleSelectPromotionParams
  | IGoogleViewPromotionParams
  | IGoogleAddToCartParams

class BaseEventParams {
  items: Item[]
  member_id?: string

  constructor(items: Item[]) {
    this.items = items
  }

  transformItemsToGoogleFormat(): GoogleItem[] {
    return this.items?.map((item) => ({
      item_id: item?.id?.toString(),
      item_siv_id: (item as any)?.sivId?.toString?.() ?? '',
      item_name: item?.name?.trim() ? item.name : 'empty-item-name',
      ...(item.brand && { item_brand: item.brand }),
      ...(item.category && { item_category: item.category }),
      ...(item.price && { price: item.price }),
      ...(item.quantity && { quantity: item.quantity }),
      ...(item.discount && { discount: item.discount }),
      ...(item.coupon && { coupon: item.coupon }),
    }))
  }

  protected cleanObject<T extends Record<string, any>>(obj: T): Partial<T> {
    // Use an intermediate type for accumulation to allow mutation
    const result = Object.entries(obj).reduce(
      (acc: Record<string, any>, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value
        }
        return acc
      },
      {},
    ) // No casting here, acc is explicitly a more mutable type.

    return result as Partial<T> // Cast the result to Partial<T> after modifications.
  }
}

export class GoogleSelectItemParams extends BaseEventParams {
  points?: number
  item_list_id?: string
  item_list_name?: string

  constructor(params: SelectItemParams) {
    super(params.items)
    this.points = params.points
    this.item_list_id = params.listId
    this.item_list_name = params.listName
    this.member_id = params.memberId
  }

  convertToGA4Format(): IGoogleSelectItemParams {
    const result = {
      items: this.transformItemsToGoogleFormat(),
      ...(this.points && { points: this.points }),
      ...(this.item_list_id && { item_list_id: this.item_list_id }),
      ...(this.item_list_name && { item_list_name: this.item_list_name }),
      member_id: this.member_id,
    }

    return this.cleanObject(result) as IGoogleSelectItemParams
  }
}

export class GoogleViewItemParams extends BaseEventParams {
  currency: string
  value: number
  points: number
  in_stock: boolean
  device_type?: string
  recommended?: boolean

  constructor(params: ViewItemParams) {
    super(params.items)
    this.currency = params.currency
    this.value = params.value
    this.points = params.points
    this.in_stock = params.inStock
    this.device_type = params.deviceType
    this.member_id = params.memberId
    this.recommended = params.recommended || false
  }

  convertToGA4Format(): IGoogleViewItemParams {
    const result = {
      items: this.transformItemsToGoogleFormat(),
      currency: this.currency,
      value: this.value,
      points: this.points,
      in_stock: this.in_stock,
      device_type: this.device_type,
      member_id: this.member_id,
      recommended: this.recommended,
    }

    return this.cleanObject(result) as IGoogleViewItemParams
  }
}

export class GoogleViewCartParams extends BaseEventParams {
  currency: string
  value: number
  cart_item_count: number
  points: number
  device_type?: string

  constructor(params: ViewCartParams) {
    super(params.items)
    this.currency = params.currency
    this.value = params.value
    this.cart_item_count = params.cartItemCount
    this.points = params.points
    this.device_type = params.deviceType
    this.member_id = params.memberId
  }

  convertToGA4Format(): IGoogleViewCartParams {
    const result = {
      items: this.transformItemsToGoogleFormat(),
      currency: this.currency,
      value: this.value,
      cart_item_count: this.cart_item_count,
      points: this.points,
      device_type: this.device_type,
      member_id: this.member_id,
    }

    return this.cleanObject(result) as IGoogleViewCartParams
  }
}

export class GoogleViewItemListParams extends BaseEventParams {
  item_list_id?: string
  item_list_name?: string

  constructor(params: ViewItemListParams) {
    super(params.items)
    this.item_list_id = params.listId
    this.item_list_name = params.listName
    this.member_id = params.memberId
  }

  convertToGA4Format(): IGoogleViewItemListParams {
    const result = {
      items: this.transformItemsToGoogleFormat(),
      ...(this.item_list_id && { item_list_id: this.item_list_id }),
      ...(this.item_list_name && { item_list_name: this.item_list_name }),
      member_id: this.member_id,
    }

    return this.cleanObject(result) as IGoogleViewItemListParams
  }
}

export class GoogleBeginCheckoutParams extends BaseEventParams {
  currency: string
  value: number
  coupon?: string
  points: number
  time_spent_on_cart: number

  constructor(params: BeginCheckoutParams) {
    super(params.items)
    this.currency = params.currency
    this.value = params.value
    this.coupon = params.coupon
    this.points = params.points
    this.time_spent_on_cart = params.timeSpentOnCart
    this.member_id = params.memberId
  }

  convertToGA4Format(): IGoogleBeginCheckoutParams {
    const result = {
      items: this.transformItemsToGoogleFormat(),
      currency: this.currency,
      value: this.value,
      points: this.points,
      time_spent_on_cart: this.time_spent_on_cart,
      ...(this.coupon && { coupon: this.currency }),
      member_id: this.member_id,
    }

    return this.cleanObject(result) as IGoogleBeginCheckoutParams
  }
}

export class GoogleAddToCartParams extends BaseEventParams {
  currency: string
  value: number
  in_stock: boolean
  time_to_add: number
  cart_total: number

  constructor(params: AddToCartParams) {
    super(params.items)
    this.currency = params.currency
    this.value = params.value
    this.in_stock = params.inStock
    this.time_to_add = params.timeToAdd
    this.cart_total = params.cartTotal
    this.member_id = params.memberId
  }

  convertToGA4Format(): IGoogleAddToCartParams {
    const result = {
      items: this.transformItemsToGoogleFormat(),
      currency: this.currency,
      value: this.value,
      in_stock: this.in_stock,
      time_to_add: this.time_to_add,
      cart_total: this.cart_total,
      member_id: this.member_id,
    }

    return this.cleanObject(result) as IGoogleAddToCartParams
  }
}

export class GooglePurchaseParams extends BaseEventParams {
  currency: string
  value: number
  revenue: number
  transaction_id: string
  points: number
  member_segment: string
  payment_method: string
  checkout_duration: number
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string

  constructor(params: PurchaseParams) {
    super(params.items)
    this.currency = params.currency
    this.value = params.value
    this.revenue = params.revenue
    this.transaction_id = params.transactionId
    this.points = params.points
    this.member_segment = params.memberSegment
    this.payment_method = params.paymentMethod
    this.checkout_duration = params.checkoutDuration
    this.member_id = params.memberId
    this.utm_source = (params as any).utm_source
    this.utm_medium = (params as any).utm_medium
    this.utm_campaign = (params as any).utm_campaign
    this.utm_content = (params as any).utm_content
    this.utm_term = (params as any).utm_term
  }

  convertToGA4Format(): IGooglePurchaseParams {
    const result = {
      items: this.transformItemsToGoogleFormat(),
      currency: this.currency,
      value: this.value,
      revenue: this.revenue,
      transaction_id: this.transaction_id,
      points: this.points,
      member_segment: this.member_segment,
      payment_method: this.payment_method,
      checkout_duration: this.checkout_duration,
      member_id: this.member_id,
      ...(this.utm_source && { utm_source: this.utm_source }),
      ...(this.utm_medium && { utm_medium: this.utm_medium }),
      ...(this.utm_campaign && { utm_campaign: this.utm_campaign }),
      ...(this.utm_content && { utm_content: this.utm_content }),
      ...(this.utm_term && { utm_term: this.utm_term }),
    }

    return this.cleanObject(result) as IGooglePurchaseParams
  }
}

export class GoogleAddPaymentInfoParams extends BaseEventParams {
  currency: string
  value: number
  payment_type: string
  tax_amount: number
  coupon?: string

  constructor(params: AddPaymentInfoParams) {
    super(params.items)
    this.currency = params.currency
    this.value = params.value
    this.payment_type = params.paymentType
    this.tax_amount = params.taxAmount
    this.member_id = params.memberId
  }

  convertToGA4Format(): IGoogleAddPaymentInfoParams {
    const result = {
      items: this.transformItemsToGoogleFormat(),
      currency: this.currency,
      value: this.value,
      payment_type: this.payment_type,
      tax_amount: this.tax_amount,
      member_id: this.member_id,
    }

    return this.cleanObject(result) as IGoogleAddPaymentInfoParams
  }
}

class GooglePromotionParams extends BaseEventParams {
  creative_name?: string
  creative_slot?: string
  promotion_id?: string
  promotion_name?: string

  constructor(params: ViewPromotionParams | SelectPromotionParams) {
    super(params.items)
    this.creative_name = params.creativeName
    this.creative_slot = params.creativeSlot
    this.promotion_id = params.promotionId
    this.promotion_name = params.promotionName
    this.member_id = params.memberId
  }

  convertToGA4Format(): IGoogleViewPromotionParams {
    const result = {
      items: this.transformItemsToGoogleFormat(),
      ...(this.creative_name && { creative_name: this.creative_name }),
      ...(this.creative_slot && { creative_slot: this.creative_slot }),
      ...(this.promotion_id && { promotion_id: this.promotion_id }),
      ...(this.promotion_name && { promotion_name: this.promotion_name }),
      member_id: this.member_id,
    }

    return this.cleanObject(result) as IGoogleViewPromotionParams
  }
}

export class GoogleLocationSuggestionParams {
  mxName: string
  form: string
  locale: string
  query: string

  constructor(params: LocationSuggestionParams) {
    this.mxName = params.mxName
    this.form = params.form
    this.locale = params.locale ?? 'en-CA'
    this.query = params.query
  }

  convertToGA4Format(): LocationSuggestionParams {
    return {
      mxName: this.mxName,
      form: this.form,
      locale: this.locale,
      query: this.query,
    }
  }
}

export class GoogleViewPromotionParams extends GooglePromotionParams {}

export class GoogleSelectPromotionParams extends GooglePromotionParams {}

export type GoogleEventData =
  | GoogleSelectItemParams
  | GoogleViewItemParams
  | GoogleViewItemListParams
  | GoogleBeginCheckoutParams
  | GooglePurchaseParams
  | GoogleViewPromotionParams
  | GoogleSelectPromotionParams
  | GoogleAddToCartParams
  | GoogleViewCartParams
  | GoogleAddPaymentInfoParams
  | GoogleLocationSuggestionParams

export interface GoogleEventParams {
  type: GoogleAnalyticEvents
  data: GoogleEventData
}
